import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ArrowRight, Camera, ChevronLeft, ChevronRight, Images, Info, MinusCircle, Pencil, Plus, RefreshCcw, ScanLine, ShieldCheck, Sparkles, Sun, User, X } from "lucide-react";

import { api, ApiError } from "@/lib/api";
import { pickFromFiles } from "@/lib/media";
import { Bullets, Button, Card, CheckRow, WaveBackdrop } from "@/components/kit";
import { Page } from "@/components/layout";
import { useCamera } from "@/components/widgets";
import { useApp } from "@/store";

const STEPS = ["Uploading securely…", "Mapping face shape…", "Reading hair pattern…", "Estimating length & density…", "Building your profile…"];
const EXTRA = [{ view: "hair_front", label: "Front hair" }, { view: "side", label: "Side hair" }];
const ERR_TITLE = {
  cancelled: "No photo selected",
  pick: "Couldn't open that photo",
  network: "Upload failed",
  timeout: "This took too long",
  no_face: "We couldn't see a face or hair",
  ai: "AI analysis unavailable",
};
export const CONSENT_SCAN = [
  "Your photo is sent through our server to Google Gemini only to create this hair estimate.",
  "It isn't stored — not by BREEZ, not on our server. It's processed in memory, then discarded.",
  "It isn't used for identity or face recognition, and isn't used for AI training.",
];

function toErr(e) {
  const code = e instanceof ApiError ? e.code : undefined;
  const message = e?.message ?? "Something went wrong. Please try again.";
  if (code === "timeout") return { kind: "timeout", message: "The scan didn't finish in time. Check your connection and try again." };
  if (code === "network") return { kind: "network", message: "Your photo couldn't be sent. Check your connection and try again." };
  if (code === "no_face") return { kind: "no_face", message };
  return { kind: "ai", message };
}

export function CameraBlocked({ testId = "scan-permission-blocked" }) {
  return (
    <div data-testid={testId} role="alert" className="rounded-xl bg-tert p-4">
      <p className="t-strong">Camera access is blocked for BREEZ AI in this browser.</p>
      <p className="t-body mt-1">To take a selfie, allow it in your browser settings: click the camera or lock icon in the address bar → Camera → Allow, then reload this page. Or upload a photo from your device instead.</p>
    </div>
  );
}

export default function Scan() {
  const navigate = useNavigate();
  const { setScanDraft, setScanPhoto } = useApp();
  const [openCamera, cameraEl] = useCamera();
  const [step, setStep] = useState("intro");
  const [consent, setConsent] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [blocked, setBlocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [extraFor, setExtraFor] = useState(null);
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const runId = useRef(0);
  const abortRef = useRef(null);

  useEffect(() => {
    if (error) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [error]);
  useEffect(() => {
    if (step !== "analysing") return;
    setElapsed(0);
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, [step]);

  const main = photos.find((p) => p.view === "front");
  const manual = () => navigate("/edit-profile?next=recs");

  const pick = async (src, view) => {
    setBlocked(false);
    setError(null);
    setBusy(true);
    const r = src === "camera" ? await openCamera(view === "front") : await pickFromFiles();
    setBusy(false);
    setExtraFor(null);
    if (r.status === "blocked") return setBlocked(true);
    if (r.status === "cancelled") {
      if (view !== "front" || main) return;
      return setError({ kind: "cancelled", message: "Choose Take selfie or Upload from device to continue." });
    }
    if (r.status === "error") return setError({ kind: "pick", message: r.message });
    setPhotos((prev) => [...prev.filter((x) => x.view !== view), { uri: r.uri, base64: r.base64, view }]);
    if (view === "front") setStep("preview");
  };

  const analyse = async () => {
    if (!main) return;
    const id = ++runId.current;
    setError(null);
    setStep("analysing");
    abortRef.current = new AbortController();
    try {
      const ordered = ["front", "hair_front", "side"].map((v) => photos.find((p) => p.view === v)).filter(Boolean);
      const result = await api("/scan", { method: "POST", body: { consent: true, images: ordered.map((p) => ({ data: p.base64, view: p.view })) }, timeoutMs: 75000, signal: abortRef.current.signal });
      if (id !== runId.current) return;
      setScanDraft(result);
      setScanPhoto({ uri: main.uri, base64: main.base64 });
      setPhotos([]);
      setConsent(false);
      setStep("intro");
      navigate("/scan-results");
    } catch (e) {
      if (id !== runId.current) return;
      setError(toErr(e));
      setStep("preview");
    }
  };
  const cancelAnalysis = () => {
    runId.current++;
    abortRef.current?.abort();
    setStep("preview");
  };
  const retry = () => {
    if (["no_face", "pick", "cancelled"].includes(error?.kind) || !main) {
      setError(null);
      setStep("source");
    } else analyse();
  };

  if (step === "analysing") {
    const progress = Math.min(0.95, elapsed / 15);
    return (
      <div data-testid="scan-analysing" className="relative flex min-h-[calc(100dvh-64px)] flex-col items-center justify-center overflow-hidden bg-navy p-6">
        <WaveBackdrop tone="dark" />
        <div className="relative flex w-full max-w-sm flex-col items-center">
          {main ? <img src={main.uri} alt="Your selfie" className="mb-6 h-[200px] w-[160px] rounded-[20px] border-2 border-ocean object-cover" /> : null}
          <p className="font-body-bold text-[11px] tracking-[1.6px] text-sky">BREEZ AI · GEMINI VISION</p>
          <p key={Math.floor(elapsed / 3)} data-testid="scan-analysing-step" aria-live="polite" className="animate-fade-in mt-2 text-center font-display text-[28px] text-white">{STEPS[Math.min(STEPS.length - 1, Math.floor(elapsed / 3))]}</p>
          <div className="mt-6 h-1.5 w-full overflow-hidden rounded bg-[rgba(255,255,255,0.45)]">
            <div className="h-1.5 rounded bg-ocean transition-all duration-1000" style={{ width: `${progress * 100}%` }} />
          </div>
          <p data-testid="scan-elapsed" className="mt-2 font-body-medium text-xs text-soft">{elapsed}s · usually 5–20 seconds</p>
          <Button testId="scan-cancel-analysis" title="Cancel" variant="glass" onClick={cancelAnalysis} className="mt-6 w-full" />
        </div>
      </div>
    );
  }

  const cancelledKind = error?.kind === "cancelled";
  const errorCard = error ? (
    <Card testId="scan-error-card" className={cancelledKind ? "!border-sky" : "!border-[#EF4444]"}>
      <div className="flex items-center gap-2" role="alert">
        {cancelledKind ? <Info className="h-5 w-5 text-navy" /> : <AlertCircle className="h-5 w-5 text-[#EF4444]" />}
        <p data-testid="scan-error-title" className={`t-title flex-1 ${cancelledKind ? "!text-navy" : "!text-[#EF4444]"}`}>{ERR_TITLE[error.kind]}</p>
      </div>
      <p data-testid="scan-error-message" className="t-body mt-2">{error.message}</p>
      <div className="mt-4 flex gap-3">
        <Button testId="scan-retry-button" title={error.kind === "no_face" || error.kind === "pick" ? "Retake" : "Try again"} variant="outline" onClick={retry} className="flex-1" />
        <Button testId="scan-error-manual-button" title="Manual" onClick={manual} className="flex-1" />
      </div>
    </Card>
  ) : null;

  return (
    <div data-testid="scan-screen">
      {cameraEl}
      <Page className="flex flex-col gap-6 py-6 md:py-10">
        {step === "intro" ? (
          <section className="animate-fade-up relative overflow-hidden rounded-[20px] bg-navy p-6 md:p-10">
            <WaveBackdrop tone="dark" />
            <div className="relative">
              <div className="mx-auto mb-6 flex h-[150px] w-[150px] items-center justify-center rounded-full border border-glass bg-glass-dark">
                <ScanLine className="h-[84px] w-[84px] text-sky" aria-hidden />
              </div>
              <p className="font-body-bold text-[11px] tracking-[1.4px] text-sky">AI FACE &amp; HAIR ESTIMATE</p>
              <h1 data-testid="scan-title" className="mt-1 font-display text-[34px] leading-[38px] text-white">SCAN YOUR LOOK</h1>
              <p className="mt-2 max-w-lg font-body text-sm leading-5 text-soft">One clear selfie gives you an estimated face shape, hair pattern, length and density — you can edit everything.</p>
              <Button testId="scan-start-button" title="Scan my face & hair" variant="secondary" icon={Camera} onClick={() => { setError(null); setStep(consent ? "source" : "consent"); }} className="mt-4 w-full md:w-auto" />
            </div>
          </section>
        ) : (
          <div className="flex items-center gap-3">
            <button type="button" data-testid="scan-step-back" aria-label="Back" onClick={() => { setError(null); setBlocked(false); setStep(step === "preview" ? "source" : step === "source" ? "consent" : "intro"); }} className="focus-ring flex h-11 w-11 items-center justify-center rounded-full bg-white text-navy shadow-card">
              <ChevronLeft className="h-[22px] w-[22px]" />
            </button>
            <span className="t-label">Step {step === "consent" ? 1 : step === "source" ? 2 : 3} of 3</span>
          </div>
        )}

        {step === "consent" ? (
          <div className="animate-fade-up flex flex-col gap-4">
            <h1 className="t-h1">BEFORE WE SCAN.</h1>
            <Card testId="scan-consent-card" className="!border-sky !bg-tert">
              <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-navy" aria-hidden /><h2 className="t-title">Your photo privacy</h2></div>
              <Bullets items={CONSENT_SCAN} />
              <div className="mt-4"><CheckRow testId="scan-consent-checkbox" checked={consent} onChange={setConsent} label="I understand and want to analyse my photo." /></div>
              <button type="button" data-testid="scan-consent-privacy-link" onClick={() => navigate("/privacy")} className="focus-ring min-h-[44px] rounded-md t-strong !text-ocean hover:underline">Read more in the Privacy centre</button>
            </Card>
            <Button testId="scan-consent-continue" title="Continue" icon={ArrowRight} disabled={!consent} onClick={() => setStep("source")} />
          </div>
        ) : null}

        {step === "source" ? (
          <div className="animate-fade-up flex flex-col gap-4">
            <h1 className="t-h1">ADD YOUR SELFIE.</h1>
            <p className="t-body">Face the camera in soft, even light with your hair visible. We&apos;ll only ask for camera access if you take a selfie.</p>
            {errorCard}
            {blocked ? <CameraBlocked /> : null}
            <Button testId="scan-take-photo" title="Take selfie" icon={Camera} loading={busy} onClick={() => pick("camera", "front")} />
            <Button testId="scan-upload-photo" title="Upload from device" variant="outline" icon={Images} disabled={busy} onClick={() => pick("library", "front")} />
          </div>
        ) : null}

        {step === "preview" && main ? (
          <div className="animate-fade-up flex flex-col gap-4">
            <h1 className="t-h1">LOOKING GOOD?</h1>
            {errorCard}
            <div className="mx-auto aspect-[3/4] w-full max-w-md overflow-hidden rounded-[20px] bg-tert">
              <img data-testid="scan-preview-image" src={main.uri} alt="Your selfie preview" className="h-full w-full object-cover" />
            </div>
            <div className="flex gap-3">
              <Button testId="scan-retake" title="Retake" icon={RefreshCcw} variant="outline" disabled={busy} onClick={() => pick("camera", "front")} className="flex-1" />
              <Button testId="scan-replace" title="Replace" icon={Images} variant="outline" disabled={busy} onClick={() => pick("library", "front")} className="flex-1" />
            </div>
            {blocked ? <CameraBlocked /> : null}
            <div className="flex flex-col gap-2">
              <span className="t-label">Optional · sharper hair estimate</span>
              <div className="flex gap-3">
                {EXTRA.map(({ view, label }) => {
                  const p = photos.find((x) => x.view === view);
                  return (
                    <div key={view} className={`relative aspect-square flex-1 overflow-hidden rounded-xl bg-tert md:max-w-[200px] ${extraFor === view ? "border-2 border-ocean" : "border border-sky"}`}>
                      <button type="button" data-testid={`scan-slot-${view}`} aria-label={`Add ${label} photo`} onClick={() => setExtraFor(extraFor === view ? null : view)} className="focus-ring absolute inset-0 flex items-center justify-center">
                        {p ? <img src={p.uri} alt={label} className="h-full w-full object-cover" /> : <Plus className="h-6 w-6 text-navy" />}
                      </button>
                      <span className={`pointer-events-none absolute inset-x-0 bottom-0 p-1.5 text-center font-body-semi text-[11px] ${p ? "bg-glass-dark text-white" : "text-navy"}`}>{label}</span>
                      {p ? (
                        <button type="button" data-testid={`scan-slot-remove-${view}`} aria-label={`Remove ${label} photo`} onClick={() => setPhotos((prev) => prev.filter((x) => x.view !== view))} className="focus-ring absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-glass-dark text-white">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              {extraFor ? (
                <div className="flex gap-3">
                  <Button testId="scan-extra-camera" title="Camera" icon={Camera} variant="outline" disabled={busy} onClick={() => pick("camera", extraFor)} className="flex-1" />
                  <Button testId="scan-extra-gallery" title="Upload" icon={Images} variant="outline" disabled={busy} onClick={() => pick("library", extraFor)} className="flex-1" />
                </div>
              ) : null}
            </div>
            <Button testId="scan-analyse-button" title="Analyse" icon={Sparkles} disabled={busy} onClick={analyse} />
            <p className="t-caption text-center">Sent to Google Gemini via BREEZ only for this estimate · not stored</p>
          </div>
        ) : null}

        <button type="button" data-testid="scan-manual-entry" onClick={manual} className="focus-ring flex items-center gap-3 rounded-[20px] border border-line bg-white p-4 text-left transition-all hover:border-ocean">
          <Pencil className="h-[22px] w-[22px] text-navy" aria-hidden />
          <span className="flex-1">
            <span className="t-title block">Enter details manually</span>
            <span className="t-caption block">No photo needed — pick your hair details yourself.</span>
          </span>
          <ChevronRight className="h-[18px] w-[18px] text-mute" aria-hidden />
        </button>

        {step === "intro" ? (
          <Card>
            <p className="t-label">For the best estimate</p>
            <ul className="mt-2 space-y-2">
              {[[Sun, "Face a window — soft, even light."], [User, "Look straight at the camera, hair off your face."], [MinusCircle, "No hats, hoods or heavy filters."]].map(([Icon, text]) => (
                <li key={text} className="flex items-center gap-2"><Icon className="h-[18px] w-[18px] text-ocean" aria-hidden /><span className="t-body">{text}</span></li>
              ))}
            </ul>
          </Card>
        ) : null}
      </Page>
    </div>
  );
}
